import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AutomationSettings } from "@/hooks/useDashboardData";

interface AutomationPanelProps {
  automation: AutomationSettings;
  loading: boolean;
  onToggleStatus: (on: boolean) => Promise<boolean>;
  onUpdateReply: (settings: { voice: boolean; image: boolean; document: boolean }) => Promise<boolean>;
}

export default function AutomationPanel({
  automation,
  loading,
  onToggleStatus,
  onUpdateReply,
}: AutomationPanelProps) {
  const { t } = useTranslation("dashboard");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const showFeedback = (type: "success" | "error", msg: string) => {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 3000);
  };

  const handleToggle = async () => {
    setSaving(true);
    const ok = await onToggleStatus(!automation.status);
    setSaving(false);
    showFeedback(ok ? "success" : "error", ok ? t("automation.statusUpdated") : t("automation.failedUpdate"));
  };

  const handleReplyToggle = async (key: "voice" | "image" | "document") => {
    const newSettings = { ...automation.reply, [key]: !automation.reply[key] };
    setSaving(true);
    const ok = await onUpdateReply(newSettings);
    setSaving(false);
    showFeedback(ok ? "success" : "error", ok ? t("automation.replySettingsUpdated") : t("automation.failedUpdate"));
  };

  const replyOptions: { key: "voice" | "image" | "document"; label: string; icon: string; desc: string }[] = [
    { key: "voice", label: t("automation.voiceMessages"), icon: "ri-mic-line", desc: t("automation.voiceDesc") },
    { key: "image", label: t("automation.imageReplies"), icon: "ri-image-line", desc: t("automation.imageDesc") },
    { key: "document", label: t("automation.documentReplies"), icon: "ri-file-text-line", desc: t("automation.documentDesc") },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-lg text-foreground-950">{t("automation.title")}</h3>
        {feedback && (
          <span className={`text-xs font-medium px-3 py-1 rounded-full ${feedback.type === "success" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
            {feedback.msg}
          </span>
        )}
      </div>

      <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
        <div className="flex items-center justify-between mb-1">
          <div>
            <p className="text-foreground-950 font-semibold text-base">{t("automation.statusTitle")}</p>
            <p className="text-foreground-500 text-xs mt-0.5">
              {automation.status ? t("automation.statusActive") : t("automation.statusPaused")}
            </p>
          </div>
          <button
            onClick={handleToggle}
            disabled={loading || saving}
            className={`relative w-14 h-7 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
              automation.status ? "bg-primary-500" : "bg-background-200"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                automation.status ? "translate-x-7" : "translate-x-0"
              }`}
            />
          </button>
        </div>
        {automation.updated_at && (
          <p className="text-foreground-400 text-xs mt-2">
            {t("automation.lastUpdated")} {new Date(automation.updated_at).toLocaleString()}
          </p>
        )}
      </div>

      <div className="bg-background-50 rounded-2xl border border-background-200/50 p-5">
        <p className="text-foreground-950 font-semibold text-base mb-4">{t("automation.replySettings")}</p>
        <div className="space-y-3">
          {replyOptions.map((opt) => (
            <div key={opt.key} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${automation.reply[opt.key] ? "bg-primary-100" : "bg-background-100"}`}>
                  <i className={`${opt.icon} text-sm ${automation.reply[opt.key] ? "text-primary-600" : "text-foreground-400"}`}></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-950">{opt.label}</p>
                  <p className="text-xs text-foreground-500">{opt.desc}</p>
                </div>
              </div>
              <button
                onClick={() => handleReplyToggle(opt.key)}
                disabled={loading || saving}
                className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
                  automation.reply[opt.key] ? "bg-primary-500" : "bg-background-200"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${
                    automation.reply[opt.key] ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}